import React, { useState, useRef, useEffect } from 'react';
import { useProjectStore } from '../../hooks/useProjectStore';
import { ECO_PARAMS, computeProduction, ETH, ecoInputStyle } from './ecoShared';

// /economics/production — Arps 指数递减产量预测 + EUR
export default function EcoProductionPage() {
  const { currentProject } = useProjectStore();
  const [, force] = useState(0);
  const p = ECO_PARAMS;
  const set = (k: keyof typeof p, v: number) => { (p[k] as number) = v; force((x) => x + 1); };
  const { annual, eur } = computeProduction(p);

  if (!currentProject) return <div style={{ padding: 40, color: ETH.textSec }}>请先选择项目</div>;

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: ETH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${ETH.headerBorder}`, display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, color: ETH.text, margin: 0 }}>产量预测 (Arps 指数递减)</h2>
        <span style={{ fontSize: 12, color: ETH.textMuted }}>q(t) = qᵢ·e^(−D·t)</span>
      </div>

      <div style={{ padding: '10px 20px', display: 'flex', gap: 16, flexWrap: 'wrap', borderBottom: `1px solid ${ETH.headerBorder}`, background: ETH.panelHeaderBg }}>
        <Lab t="初始日产 万m³/d"><input type="number" value={p.qi} style={ecoInputStyle} onChange={(e) => set('qi', +e.target.value)} /></Lab>
        <Lab t="年递减率 %"><input type="number" value={p.declineRate} style={ecoInputStyle} onChange={(e) => set('declineRate', +e.target.value)} /></Lab>
        <Lab t="评价年限 年"><input type="number" value={p.years} style={ecoInputStyle} onChange={(e) => set('years', Math.max(1, Math.min(30, +e.target.value)))} /></Lab>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: ETH.textMuted }}>累计产量 EUR</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: ETH.ok, fontFamily: ETH.fontMono }}>
            {(eur / 1e4).toFixed(2)}<span style={{ fontSize: 12, fontWeight: 400, color: ETH.textSec, marginLeft: 4 }}>亿m³</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 280px', gap: 1, background: ETH.headerBorder }}>
        <div style={{ background: ETH.canvasBg }}><DeclineChart annual={annual} /></div>
        <div style={{ background: ETH.canvasBg, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead><tr style={{ background: ETH.panelHeaderBg, position: 'sticky', top: 0 }}>
              {['年', '年产 万m³', '累计 万m³'].map((h) => <th key={h} style={{ padding: '5px 8px', textAlign: 'right', borderBottom: `1px solid ${ETH.headerBorder}`, color: ETH.textSec }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {annual.map((a) => (
                <tr key={a.year} style={{ borderBottom: '1px solid #F0EDE4' }}>
                  <td style={td}>{a.year}</td>
                  <td style={td}>{a.rate.toFixed(0)}</td>
                  <td style={td}>{a.cum.toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DeclineChart({ annual }: { annual: { year: number; rate: number; cum: number }[] }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 2;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * dpr; cv.height = rect.height * dpr; ctx.scale(dpr, dpr);
    const W = rect.width, H = rect.height;
    ctx.fillStyle = ETH.canvasBg; ctx.fillRect(0, 0, W, H);
    if (!annual.length) return;
    const padL = 50, padR = 50, padT = 24, padB = 32;
    const plotW = W - padL - padR, plotH = H - padT - padB;
    const rMax = Math.max(...annual.map((a) => a.rate)) * 1.1;
    const cMax = Math.max(...annual.map((a) => a.cum)) * 1.05;
    const n = annual.length;
    const bw = plotW / n * 0.6;
    const xAt = (i: number) => padL + (i + 0.5) * (plotW / n);

    ctx.fillStyle = ETH.textMuted; ctx.font = `9px ${ETH.fontMono}`;
    for (let k = 0; k <= 4; k++) {
      const y = padT + (k / 4) * plotH;
      ctx.strokeStyle = ETH.grid; ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(W - padR, y); ctx.stroke();
      ctx.textAlign = 'right'; ctx.fillStyle = ETH.stage; ctx.fillText((rMax * (1 - k / 4)).toFixed(0), padL - 4, y + 3);
      ctx.textAlign = 'left'; ctx.fillStyle = ETH.ok; ctx.fillText((cMax * (1 - k / 4) / 1e4).toFixed(1), W - padR + 4, y + 3);
    }
    // 年产柱
    annual.forEach((a, i) => {
      const h = (a.rate / rMax) * plotH;
      ctx.fillStyle = ETH.stage;
      ctx.fillRect(xAt(i) - bw / 2, padT + plotH - h, bw, h);
      if (n <= 20) { ctx.fillStyle = ETH.textMuted; ctx.font = `8px ${ETH.fontMono}`; ctx.textAlign = 'center'; ctx.fillText(`${a.year}`, xAt(i), H - padB + 12); }
    });
    // 累计线
    ctx.strokeStyle = ETH.ok; ctx.lineWidth = 2; ctx.beginPath();
    annual.forEach((a, i) => { const y = padT + plotH - (a.cum / cMax) * plotH; i === 0 ? ctx.moveTo(xAt(i), y) : ctx.lineTo(xAt(i), y); });
    ctx.stroke();
    // 轴标
    ctx.fillStyle = ETH.stage; ctx.font = `10px ${ETH.fontUI}`; ctx.textAlign = 'left';
    ctx.fillText('年产(万m³)', padL, padT - 10);
    ctx.fillStyle = ETH.ok; ctx.textAlign = 'right'; ctx.fillText('累计(亿m³)', W - padR, padT - 10);
  }, [annual]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}

function Lab({ t, children }: { t: string; children: React.ReactNode }) {
  return <label style={{ fontSize: 11, color: ETH.textSec, display: 'flex', flexDirection: 'column', gap: 3 }}>{t}{children}</label>;
}
const td: React.CSSProperties = { padding: '4px 8px', textAlign: 'right', fontFamily: ETH.fontMono, color: ETH.text };
